import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { PagesDropdown } from '@/components/layout/pages-dropdown'

import { navLinks } from '@/constants/navigation'

// Navbar uses the compact PagesDropdown (see pages-dropdown.tsx); the legacy
// full-width mega-menu was retired during the dropdown redesign.
export const Navbar: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth > 991) setMobileOpen(false)
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    // Lock body scroll while the mobile overlay menu is open.
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : ''
        return () => {
            document.body.style.overflow = ''
        }
    }, [mobileOpen])

    const closeMobile = () => setMobileOpen(false)

    return (
        <div className='fixed-navbar'>
            <div
                className={cn('nav-rounded-center w-nav', scrolled && 'nav-scrolled')}
                role='banner'
                data-collapse='medium'
            >
                <div className='container nav-container-rounded-center'>
                    <div className='nav-elements-holder'>
                        <div className='nav-menu-parent-rounded-center'>
                            <Link href='/' className='brand-link w-nav-brand' aria-label='Manila home'>
                                <img
                                    src='/images/Logo.svg'
                                    loading='lazy'
                                    alt='Manila'
                                    className='brand-logo-rounded-nav'
                                />
                            </Link>

                            <nav
                                className='nav-menu w-nav-menu'
                                role='navigation'
                                {...(mobileOpen ? { 'data-nav-menu-open': '' } : {})}
                            >
                                {navLinks.map((link) => (
                                    <div className='nav-link-holder' key={link.href}>
                                        <a href={link.href} className='nav-link w-inline-block' onClick={closeMobile}>
                                            <div>{link.label}</div>
                                            <div className='link-background' />
                                        </a>
                                    </div>
                                ))}

                                <div className='button-nav-holder mobile'>
                                    <a href='/#cta' className='button-gradient-nav w-inline-block' onClick={closeMobile}>
                                        <div>Get demo</div>
                                        <div className='button-gradient-bg' />
                                    </a>
                                </div>

                                <PagesDropdown onNavigate={closeMobile} />
                            </nav>

                            <div className='nav-cart-holder'>
                                <div
                                    className={cn('menu-button w-nav-button', mobileOpen && 'w--open')}
                                    role='button'
                                    tabIndex={0}
                                    aria-label='Menu'
                                    aria-expanded={mobileOpen}
                                    onClick={() => setMobileOpen((prev) => !prev)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault()
                                            setMobileOpen((prev) => !prev)
                                        }
                                    }}
                                >
                                    <img
                                        src='/images/menu-icon.svg'
                                        loading='lazy'
                                        alt=''
                                        className='menu-icon'
                                    />
                                </div>
                            </div>

                            <div className='button-nav-holder'>
                                <a href='/#cta' className='button-gradient-nav w-inline-block'>
                                    <div>Get demo</div>
                                    <div className='button-gradient-bg' />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className='background-nav' />
                </div>
            </div>
        </div>
    )
}
